import React from 'react';
import {
  Box, Center, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import {
  BookUser, Check, Contact, GraduationCap, UserRound, X,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import type { IdCaptureMode } from './IdCaptureGuideOverlays';

type ProgressItemProps = { index: number; label: string; state: 'done' | 'active' | 'upcoming' };

function ProgressItem({ index, label, state }: ProgressItemProps) {
  const isActive = state === 'active';
  const isDone = state === 'done';
  return (
    <HStack space="xs" alignItems="center">
      <Center
        w={22}
        h={22}
        borderRadius="$full"
        bg={isActive ? '$brandBase' : isDone ? '$statusSuccessSubtle' : 'transparent'}
        borderWidth={isActive || isDone ? 0 : 1.5}
        borderColor="$borderMedium"
      >
        {isDone ? (
          <Icon as={Check} size="2xs" color="$statusSuccess" />
        ) : (
          <Text size="2xs" fontFamily="$heading" color={isActive ? '$textOnBrand' : '$textDisabled'}>
            {index}
          </Text>
        )}
      </Center>
      <Text
        size="sm"
        fontFamily={isActive ? '$heading' : undefined}
        color={isActive ? '$textHeading' : isDone ? '$textBody' : '$textDisabled'}
      >
        {label}
      </Text>
    </HStack>
  );
}

/** Two-step progress: ID document, then face. */
export function IdCheckProgress({ current }: { current: IdCaptureMode }) {
  const { t } = useTranslation();
  return (
    <HStack space="sm" alignItems="center" testID="oral-exam-id-check-progress">
      <ProgressItem
        index={1}
        label={t('oralExam.idCapture.progress.document')}
        state={current === 'document' ? 'active' : 'done'}
      />
      <Box w={28} h={1.5} bg="$borderMedium" />
      <ProgressItem
        index={2}
        label={t('oralExam.idCapture.progress.face')}
        state={current === 'selfie' ? 'active' : 'upcoming'}
      />
    </HStack>
  );
}

const ACCEPTED_KEYS = ['passport', 'drivingLicence', 'studentId'] as const;
const ACCEPTED_ICONS = {
  passport: BookUser,
  drivingLicence: Contact,
  studentId: GraduationCap,
} as const;

/** Example ID documents so "ID card" is not ambiguous. */
export function AcceptedDocuments() {
  const { t } = useTranslation();
  return (
    <HStack
      space="sm"
      flexWrap="wrap"
      justifyContent="center"
      alignItems="center"
      testID="oral-exam-id-accepted-documents"
    >
      {ACCEPTED_KEYS.map((key) => (
        <HStack
          key={key}
          space="xs"
          alignItems="center"
          px="$3"
          py="$1.5"
          borderRadius="$full"
          bg="$bgBrandSubtle"
        >
          <Icon as={ACCEPTED_ICONS[key]} size="sm" color="$brandBase" />
          <Text size="sm" color="$textOnBrandSubtle">
            {t(`oralExam.idCapture.document.accepted.${key}`)}
          </Text>
        </HStack>
      ))}
      <Text size="sm" color="$textSubtle">{t('oralExam.idCapture.document.acceptedMore')}</Text>
    </HStack>
  );
}

function Badge({ ok }: { ok: boolean }) {
  return (
    <Center
      position="absolute"
      right={-8}
      bottom={-8}
      w={24}
      h={24}
      borderRadius="$full"
      bg={ok ? '$statusSuccess' : '$statusError'}
      borderWidth={2}
      borderColor="$bgCard"
    >
      <Icon as={ok ? Check : X} size="xs" color="$textInverted" />
    </Center>
  );
}

/** Live face vs portrait on the ID — face step guidance. */
export function FaceDoDont() {
  const { t } = useTranslation();
  return (
    <HStack space="2xl" justifyContent="center" alignItems="flex-end" testID="oral-exam-id-face-do-dont">
      <VStack space="sm" alignItems="center" width={180}>
        <Box position="relative">
          <Center w={48} h={62} borderRadius="$full" borderWidth={2.5} borderColor="$statusSuccess" bg="$statusSuccessSubtle">
            <Icon as={UserRound} size="xl" color="$statusSuccess" />
          </Center>
          <Badge ok />
        </Box>
        <Text size="sm" textAlign="center">{t('oralExam.idCapture.selfie.doLabel')}</Text>
      </VStack>
      <VStack space="sm" alignItems="center" width={180}>
        <Box position="relative">
          <HStack
            w={92}
            h={58}
            borderRadius="$md"
            borderWidth={2}
            borderColor="$statusError"
            bg="$statusErrorSubtle"
            p="$1.5"
            space="xs"
            alignItems="center"
          >
            <Center w={30} h={40} borderRadius="$sm" bg="$bgCard">
              <Icon as={UserRound} size="md" color="$statusError" />
            </Center>
            <VStack space="xs" flex={1}>
              <Box h={4} borderRadius="$full" bg="$statusError" opacity={0.5} />
              <Box h={4} w="70%" borderRadius="$full" bg="$statusError" opacity={0.35} />
              <Box h={4} w="50%" borderRadius="$full" bg="$statusError" opacity={0.35} />
            </VStack>
          </HStack>
          <Badge ok={false} />
        </Box>
        <Text size="sm" textAlign="center">{t('oralExam.idCapture.selfie.dontLabel')}</Text>
      </VStack>
    </HStack>
  );
}
